/**
 * Breadcrumb Component - Feature 1: Navigation Overhaul
 * Navigation breadcrumbs for showing user's location in the app
 */
import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import clsx from 'clsx'
import './Breadcrumb.css'

export interface BreadcrumbItem {
  label: string
  href?: string
  icon?: ReactNode
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[]
  showHome?: boolean
  separator?: ReactNode
  className?: string
}

// Route to breadcrumb mapping
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/watchlist': 'Watchlist',
  '/screener': 'Screener',
  '/portfolio': 'Portfolio',
  '/alerts': 'Alerts',
  '/news': 'News & Intel',
  '/options': 'Options Hub',
  '/options-analysis': 'Options Analysis',
  '/earnings': 'Earnings Hub',
  '/market-events': 'Market Events',
  '/charts': 'Advanced Charts',
  '/backtest': 'Backtester',
  '/social': 'Social & Alt Data',
  '/scores': 'Score Explorer',
  '/strategies': 'Strategies',
  '/settings': 'Settings',
  '/stock': 'Stock',
  '/research': 'Research',
  '/strategy': 'Strategy',
  // Markets Hub
  '/markets': 'Markets',
  '/markets/equities': 'Equities',
  '/markets/etfs': 'ETFs',
  '/markets/fixed-income': 'Fixed Income',
  '/markets/commodities': 'Commodities',
  '/markets/crypto': 'Crypto',
  '/markets/fx': 'FX',
  '/markets/dividends': 'Dividends',
  '/markets/internals': 'Market Internals',
  // Intelligence Hub
  '/intelligence': 'Intelligence',
  '/intelligence/brief': 'Daily Brief',
  '/intelligence/analysis': 'AI Analysis',
  '/intelligence/news': 'News Impact',
  '/intelligence/causal': 'Causal Network',
}

export function Breadcrumb({
  items,
  showHome = true,
  separator = <ChevronRight size={14} />,
  className
}: BreadcrumbProps) {
  const location = useLocation()

  // Auto-generate breadcrumbs if not provided
  const breadcrumbItems: BreadcrumbItem[] = items || generateBreadcrumbs(location.pathname)

  // Add home if requested
  const allItems: BreadcrumbItem[] = showHome
    ? [{ label: 'Home', href: '/', icon: <Home size={14} /> }, ...breadcrumbItems]
    : breadcrumbItems

  if (allItems.length <= 1 && showHome) {
    return null // Don't show breadcrumb on home page
  }

  return (
    <nav aria-label="Breadcrumb" className={clsx('breadcrumb', className)}>
      <ol className="breadcrumb-list">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1

          return (
            <li key={index} className="breadcrumb-item">
              {index > 0 && (
                <span className="breadcrumb-separator" aria-hidden="true">
                  {separator}
                </span>
              )}

              {isLast || !item.href ? (
                <span
                  className={clsx('breadcrumb-text', isLast && 'current')}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                  <span>{item.label}</span>
                </span>
              ) : (
                <Link to={item.href} className="breadcrumb-link">
                  {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                  <span>{item.label}</span>
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean)
  const items: BreadcrumbItem[] = []

  let currentPath = ''

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    currentPath += `/${segment}`

    // Check for known route labels
    const label = ROUTE_LABELS[currentPath]

    if (label) {
      // For hub routes, the parent hub is clickable, but the tab itself is current
      const isHubTab = (segments[0] === 'markets' || segments[0] === 'intelligence') && i > 0
      items.push({
        label,
        href: i < segments.length - 1 || (isHubTab && i === 0) ? currentPath : undefined
      })
    } else {
      // For dynamic segments like /stock/AAPL, capitalize the segment
      const isStockTicker = segments[i - 1] === 'stock'
      const isEquitiesTicker = segments[i - 1] === 'equities'
      items.push({
        label: (isStockTicker || isEquitiesTicker) ? segment.toUpperCase() : formatSegment(segment),
        href: i < segments.length - 1 ? currentPath : undefined
      })
    }
  }

  return items
}

function formatSegment(segment: string): string {
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Simple page header with breadcrumb
interface PageHeaderProps {
  title: string
  subtitle?: string
  breadcrumbItems?: BreadcrumbItem[]
  actions?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbItems,
  actions,
  className
}: PageHeaderProps) {
  return (
    <div className={clsx('page-header', className)}>
      <Breadcrumb items={breadcrumbItems} />
      <div className="page-header-content">
        <div className="page-header-text">
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="page-header-actions">{actions}</div>}
      </div>
    </div>
  )
}

export default Breadcrumb
